import { DynamicFormRef, makeFormInputsUneditable } from "@mavichpe/dynamic-forms";
import { useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import GenericForm, { ConfirmationConfig } from "../GenericForm";
import { DebitnoteApi, DebitnoteDataType } from "@/api/DebitnoteApi";
import { Col, Row } from "reactstrap";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEDocumentSummary } from "@/store/slices/utils/eDocumentSelectors";
import { clearDocument, clearDocumentReferences, loadProducts, setDocumentNoteTotal, setDocumentReferences, setInvoiceReference } from "@/store/slices/eDocumentSlides";
import LoadingScreen from "@components/content/LoadingScreen";
import EdocumentProductList from "@components/dynamic/EdocumentProductList";
import { showAlert } from "@utils/Alert";
import { omitProperties } from "@utils/Utils";
import { getCurrentDateTime } from "@utils/Api";
import { DebitnoteProductDataType } from "@/api/DebitnoteProductApi";
import formConfig from "./form";
import { sendEDebitnoteData } from "@utils/EDocument";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { InvoiceFormData, InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { ClientApi } from "@/api/ClientApi";
import { CreditNoteProductRelatedAttributes, EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { CurrencyIdPubSub } from "@utils/pubsubs/PubSub";
import { flushDocumentProductEdits } from "@utils/eDocumentFlush";
import debounce from "debounce";
import { getSelectProductTotal } from "@/store/slices/utils/eDocumentUtils";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { generateAndUploadPdf } from "@utils/htmlExport";
import {
    StoredReference,
    buildAutoReferenceFromInvoice,
    serializeStoredReferences,
    toInfoRefElements,
} from "@utils/DocumentReferences";

type DebitnoteFormProps = {
  id?: string;
  formId: string,
  invoiceId: string,
  saveRedirectUrl: string,
}

const DebitnoteForm = (props: DebitnoteFormProps) => {
  const dispatch = useAppDispatch()
  const { invoiceId } = props
  const formRef = useRef<DynamicFormRef>(null);
  const invoiceData = useAppSelector((state) => state.eDocument.invoiceReference);
  const documentReferences = useAppSelector((state) => state.eDocument.documentReferences);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const suscriptionData = useAppSelector((state) => state.suscription.suscription);
  const eDocument = useAppSelector((state) => state.eDocument);
  const documentCurrency = useAppSelector((state) => state.currency);
  const [easyposEInvoice, setEasyposEInvoice] = useState<EasyposEInvoice>()

  useEffect(() => {
    if (suscriptionData) {
      setEasyposEInvoice(new EasyposEInvoice(suscriptionData))
    }
  }, [suscriptionData]);

  useEffect(() => {
    clearForm()
  }, [])

  const loadDefaultData = async () => {
    setIsLoading(true)
    try {
      const invoice = await InvoiceApi.get(invoiceId, {
        selectionSet: [
          ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "invoiceProducts.*",
          "invoiceProducts.product.*",
        ]
      })

      dispatch(setInvoiceReference(invoice))
      const defaultRazon = `Nota de débito sobre factura #${invoice.numero || ""}`
      const defaultRef = buildAutoReferenceFromInvoice(invoice, "notaDeDebito", defaultRazon)
      dispatch(setDocumentReferences([defaultRef]))
      const { id, ...restInvoice } = invoice

      const client = await ClientApi.get(`${invoice.clientId}`)
      formRef.current?.resetFormValue({
        ...restInvoice,
        client_code: client.codigo
      })
      if (invoice.invoiceProducts) {
        const productos = invoice.invoiceProducts.map((ip: EDocumentProductDataType) => {
          let product = {
            ...ip,
          }
          product = {
            ...product,
            cantFact: ip.cant,
            cant: 0,
            selected: "true"

          }
          return product
        })
        CurrencyIdPubSub.publish({ currencyId: `${invoice.currencyId}` })
        dispatch(loadProducts(productos))
      }
    } catch (err) {
      showAlert("danger", "No fue possible cargar la informacion de la factura")
    } finally {
      setIsLoading(false)
    }
  }

  const createDebitnote = async (formData: InvoiceDataType | DebitnoteDataType): Promise<InvoiceDataType> => {
    let description
    if ("description" in formData) {
      description = formData.description
    }

    let debitnoteData: DebitnoteDataType = {}
    const eDocumentState = flushDocumentProductEdits();
    const documentSummaryState = selectEDocumentSummary(eDocumentState);
    const { documentProducts } = eDocumentState

    if (documentProducts?.length == 0) {
      throw Error("Por favor agregue algun producto a la nota de debito")
    }

    if (documentProducts?.filter(p => Number(p.cant || 0) > 0).length == 0) {
      throw Error("Por favor ingrese la cantidad devuelta de productos antes de continuar")
    }

    if (!documentCurrency) {
      throw Error("Por favor Selecciona una moneda")
    }

    if (!description) {
      throw Error("Por favor Ingrese la razon de la Nota / Devolucion")
    }

    if (!invoiceData) {
      throw Error("La informacion de la factura no se ha cargado")
    }

    if (Number(documentSummaryState.descuento || 0) > 0) {
      debitnoteData.discountCode = invoiceData.discountCode
    }

    debitnoteData = {
      ...debitnoteData,
      description,
      exchange_rate: invoiceData.exchange_rate,
      activitycode: invoiceData.activitycode,
      selected_logo: invoiceData.selected_logo,
      clientId: invoiceData.clientId,
      currencyId: invoiceData.currencyId,
      subtotal: documentSummaryState.subtotal,
      descuento: documentSummaryState.descuento,
      impuestos: documentSummaryState.impuestos,
      total: documentSummaryState.total,
      created: getCurrentDateTime(),
      invoiceId: invoiceData.id
    }

    if (documentProducts) {
      const products = generateDebitnoteProducts(documentProducts)
      const createdDebitnote = await DebitnoteApi.createDebitnoteAndProducts(debitnoteData, products)

      if (easyposEInvoice && suscriptionData?.einvoice_status) {
        const refsToSend: StoredReference[] = documentReferences.length > 0
          ? (documentReferences as StoredReference[])
          : [
              buildAutoReferenceFromInvoice(
                invoiceData,
                "notaDeDebito",
                `${createdDebitnote.description || ""}`,
              ),
            ]
        if (createdDebitnote.id) {
          await DebitnoteApi.update(createdDebitnote.id, {
            info_references: serializeStoredReferences(refsToSend),
          })
        }
        await sendEDebitnoteData(easyposEInvoice, `${createdDebitnote.id}`, toInfoRefElements(refsToSend))
      }

      clearForm()
      showAlert("success", "La Nota de debito ha sido creada existosamente")
      generateAndUploadPdf(`/user/debitnotes/print/${createdDebitnote.id}/0/1`, `${suscriptionData?.numero}/pdfs/Debitnote/${createdDebitnote.document_key}.pdf`)
      return createdDebitnote;
    }

    return {} as DebitnoteDataType;
  }

  const generateDebitnoteProducts = (documentProducts: EDocumentProductDataType[]): DebitnoteProductDataType[] => {
    return documentProducts.filter(dp => dp.cant != 0).map(dp => {
      const product = omitProperties(dp, [...CreditNoteProductRelatedAttributes, "id"]) as DebitnoteProductDataType
      return product
    });
  }

  const clearForm = async () => {
    dispatch(clearDocument())
    dispatch(clearDocumentReferences())
    loadDefaultData()
  }

  const getFormConfig = () => {
    return makeFormInputsUneditable(formConfig)
  }

  const trigerMontoNotaChange = debounce((descuentoTotalNota: number) => {
    const documentProducts = eDocument.documentProducts || []
    const selectedLines = documentProducts.filter(p => p.selected == "true")
    const productTotal = getSelectProductTotal(selectedLines);

    if (productTotal.valueOf() < descuentoTotalNota) {
      showAlert("danger", "El monto total de las lineas seleccionadas es menor al valor de descuento, por favor revise la seleccion de lineas");
      return false;
    }
    dispatch(setDocumentNoteTotal(descuentoTotalNota))
  }, 500)

  const onInputUpdate = (name: keyof InvoiceFormData | "montoNota", value: any) => {
    if (name == "montoNota") {
      const descuentoTotalNota = Number(value || 0);
      trigerMontoNotaChange(descuentoTotalNota)
    }
  }

  const getCreateConfirmation = (): ConfirmationConfig | undefined => {
    const { ask_confirm = false } = suscriptionData || {}
    if (ask_confirm) {
      return {
        message: "Esta seguro que desea crear la Nota de Debito"
      }
    }
  }

  return (
    <>
      {formConfig?.sections && (<GenericForm<InvoiceFormData, InvoiceDataType>
        ref={formRef}
        formId={props.formId}
        confirmationConfig={getCreateConfirmation()}
        modelRelativeObjects={InvoiceRelatedAttributes}
        formConfig={getFormConfig()}
        getById={(...arg) => DebitnoteApi.get(...arg)}
        createFn={(...arg) => createDebitnote(...arg)}
        onInputChange={onInputUpdate}
      >
        <Row>
          <Col>
            <h3>
              Productos
            </h3>
          </Col>
        </Row>
        <EdocumentProductList
          documentNoteTable />
      </GenericForm >
      )}
      <LoadingScreen isLoading={isLoading} />
    </>

  )
}

export default DebitnoteForm;